import React, { useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import AdminHealthTab from './AdminHealthTab';
import AdminUsersTab from './AdminUsersTab';
import AdminCostTab from './AdminCostTab';
import AdminTelemetryTab from './AdminTelemetryTab';
import AdminLLMTab from './AdminLLMTab';
import AdminRadarTab from './AdminRadarTab';
import AdminWaitlistTab from './AdminWaitlistTab';
import {
  Users, Pulse, Receipt, ChartBar, Brain, Bell, Queue,
  Shield, SignOut, ArrowSquareOut,
} from "@phosphor-icons/react";

const VALID_TABS = ['health', 'users', 'waitlist', 'cost', 'telemetry', 'llm', 'radar'];

export default function AdminPanel() {
  const { isAdmin, loading, logout } = useApp();
  const navigate = useNavigate();
  const { tab } = useParams();
  const activeTab = VALID_TABS.includes(tab) ? tab : 'health';

  // Keep URL and active tab in sync: unknown tabs redirect to /admin/health
  useEffect(() => {
    if (tab && !VALID_TABS.includes(tab)) {
      navigate('/admin/health', { replace: true });
    }
  }, [tab, navigate]);

  const setActiveTab = (nextTab) => navigate(`/admin/${nextTab}`);

  const handleLogout = async () => {
    await logout();
    navigate('/auth', { replace: true });
  };

  if (loading.auth) return <div className="p-8 text-white">Verifying Admin Access...</div>;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="h-screen flex flex-col bg-[#09090b] text-zinc-300 overflow-hidden">
      {/* Header — two rows so all 6 tabs always fit */}
      <header className="px-8 pt-5 pb-0 border-b border-white/5 shrink-0">
        {/* Row 1: logo + actions */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-['Outfit'] font-bold text-white tracking-tight flex items-center gap-3">
              <Shield size={24} className="text-amber-500" weight="fill" />
              Sankhya Forge <span className="text-xs font-mono font-medium px-2 py-0.5 rounded bg-amber-500/10 text-amber-500 uppercase tracking-widest border border-amber-500/20">Admin v7.2</span>
            </h1>
            <p className="text-xs text-zinc-500 mt-0.5">Platform Governance, User Management & Observability</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 border border-transparent hover:border-amber-500/20 transition-all group"
              title="Open main app"
            >
              <ArrowSquareOut size={16} className="group-hover:scale-110 transition-transform" />
              <span className="font-medium">Open App</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-zinc-500 hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/20 transition-all group"
            >
              <SignOut size={16} className="group-hover:rotate-12 transition-transform" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
        {/* Row 2: tab bar — full width, scrollable if ever needed */}
        <div className="flex bg-white/5 p-1 rounded-t-lg border border-b-0 border-white/5 overflow-x-auto">
          <TabButton id="health"     icon={Pulse}     active={activeTab} set={setActiveTab}>Health</TabButton>
          <TabButton id="users"      icon={Users}     active={activeTab} set={setActiveTab}>Users</TabButton>
          <TabButton id="waitlist"   icon={Queue}     active={activeTab} set={setActiveTab}>Waitlist</TabButton>
          <TabButton id="cost"       icon={Receipt}   active={activeTab} set={setActiveTab}>Cost</TabButton>
          <TabButton id="telemetry"  icon={ChartBar}  active={activeTab} set={setActiveTab}>Telemetry</TabButton>
          <TabButton id="llm"        icon={Brain}     active={activeTab} set={setActiveTab}>LLM</TabButton>
          <TabButton id="radar"      icon={Bell}      active={activeTab} set={setActiveTab}>Radar</TabButton>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto p-8">
        <div className="max-w-6xl mx-auto">
          
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
            {activeTab === 'health'    && <AdminHealthTab />}
            {activeTab === 'users'     && <AdminUsersTab />}
            {activeTab === 'waitlist'  && <AdminWaitlistTab />}
            {activeTab === 'cost'      && <AdminCostTab />}
            {activeTab === 'telemetry' && <AdminTelemetryTab />}
            {activeTab === 'llm'       && <AdminLLMTab />}
            {activeTab === 'radar'     && <AdminRadarTab />}
          </div>

        </div>
      </main>
    </div>
  );
}

function TabButton({ id, icon: Icon, children, active, set }) {
  const isActive = active === id;
  return (
    <button
      onClick={() => set(id)}
      className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
        isActive 
          ? 'bg-amber-500 text-black shadow-lg shadow-amber-500/20' 
          : 'text-zinc-500 hover:text-white hover:bg-white/5'
      }`}
    >
      <Icon size={16} weight={isActive ? "fill" : "regular"} />
      {children}
    </button>
  );
}
